import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import { PageHeader, DataTable, EmptyState } from "@/components/ui-system";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, PackageX, RotateCw, Undo2, ShieldAlert, AlertTriangle } from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";
import { can } from "@/lib/permissions";

const PAGE_SIZE = 50;
const inr = (n) => `₹${Number(n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const errDetail = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return e?.response?.data?.message || fb;
};

const ACTIONS = [
  { id: "reattempt", label: "Reattempt delivery", icon: RotateCw, desc: "Ask the courier to try delivering again." },
  { id: "return", label: "Return to origin (RTO)", icon: Undo2, desc: "Give up on delivery — send the parcel back." },
  { id: "fake_attempt", label: "Report fake attempt", icon: ShieldAlert, desc: "Dispute a falsely-marked failed delivery (not available for every courier)." },
];

const B2CNdr = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const canAct = can(user, "b2c.ndr:act");

  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [notEnabled, setNotEnabled] = useState(false);

  // Action modal
  const [target, setTarget] = useState(null);
  const [action, setAction] = useState("reattempt");
  const [form, setForm] = useState({ comments: "", phone: "", address: "", addressType: "", preferredDate: "", proofImageUrl: "", remarks: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchNdr = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (search) params.search = search;
      const { data: res } = await axios.get(`${API}/b2c/seller/ndr`, { params });
      setData(res);
      setNotEnabled(false);
    } catch (e) {
      if (e?.response?.status === 422) setNotEnabled(true);
      else toast.error(errDetail(e, "Failed to load NDR shipments"));
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { fetchNdr(); }, [fetchNdr]);
  useEffect(() => { setPage(1); }, [search]);

  const openAction = (row) => {
    setTarget(row);
    setAction("reattempt");
    setForm({ comments: "", phone: row.buyer_phone || "", address: row.buyer_address || "", addressType: "", preferredDate: "", proofImageUrl: "", remarks: "" });
  };

  const submit = async () => {
    if (!target) return;
    if (!form.comments.trim()) { toast.error("Add a comment for this action."); return; }
    if (action === "fake_attempt" && (!form.proofImageUrl.trim() || !form.remarks.trim())) {
      toast.error("A fake-attempt dispute needs a proof image URL and remarks.");
      return;
    }
    setSaving(true);
    try {
      const payload = { action, comments: form.comments.trim() };
      if (action === "reattempt") {
        if (form.phone.trim()) payload.phone = form.phone.trim();
        if (form.address.trim()) payload.address = form.address.trim();
        if (form.addressType) payload.address_type = form.addressType;
        if (form.preferredDate) payload.preferred_date = form.preferredDate;
      }
      if (action === "fake_attempt") {
        payload.proof_image_url = form.proofImageUrl.trim();
        payload.remarks = form.remarks.trim();
      }
      await axios.post(`${API}/b2c/seller/ndr/${target.id}/action`, payload);
      toast.success("Action submitted — the courier will process it; the status updates as tracking syncs.");
      setTarget(null);
      fetchNdr();
    } catch (e) {
      toast.error(errDetail(e, "Could not submit the NDR action"));
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));

  const columns = [
    { key: "order", header: "Order", primary: true,
      cell: (r) => <button onClick={() => navigate(`/seller/b2c/orders/${r.id}`)} className="text-sm font-medium text-brand hover:underline">{r.order_number}</button> },
    { key: "customer", header: "Customer",
      cell: (r) => <div className="text-sm"><div className="font-medium text-slate-800">{r.buyer_name || "-"}</div><div className="text-xs text-slate-500">{[r.buyer_city, r.buyer_phone].filter(Boolean).join(" · ")}</div></div> },
    { key: "courier", header: "Courier", hideOnMobile: true, cell: (r) => <span className="text-sm text-slate-700">{r.courier_name || "-"}</span> },
    { key: "payment", header: "Payment", hideOnMobile: true,
      cell: (r) => r.payment_mode === "cod"
        ? <span className="text-sm font-medium text-amber-700">COD {inr(r.cod_amount)}</span>
        : <span className="text-sm text-slate-600">Prepaid</span> },
    { key: "reason", header: "NDR reason", cell: (r) => <span className="text-sm text-slate-700">{r.ndr_reason || <span className="text-slate-400">—</span>}</span> },
    { key: "lastaction", header: "Last action", hideOnMobile: true,
      cell: (r) => {
        const a = (r.ndr_actions || [])[r.ndr_actions.length - 1];
        return a ? <div className="text-xs text-slate-600"><Badge variant="outline" className="border-slate-200">{a.action}</Badge><div className="mt-0.5 text-slate-400">{formatToIST(a.at)}</div></div> : <span className="text-slate-400 text-sm">—</span>;
      } },
    { key: "actions", header: "Actions", isActions: true,
      cell: (r) => canAct ? <Button size="sm" onClick={() => openAction(r)}><PackageX className="w-3.5 h-3.5 mr-1" /> Take action</Button> : null },
  ];

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6 w-full">
        <PageHeader title="NDR — Failed Deliveries" subtitle="Undelivered shipments — reattempt, return (RTO), or dispute a fake attempt" />

        {notEnabled ? (
          <Card className="border-0 shadow-sm"><CardContent className="p-0">
            <EmptyState icon={PackageX} title="B2C is not enabled yet" description="Your account doesn't have a B2C plan configured. Ask your account manager." />
          </CardContent></Card>
        ) : (
          <>
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-6">
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                  <Input placeholder="Search by order #, AWB or buyer phone..." value={searchInput} onChange={(e) => setSearchInput(e.target.value)} className="pl-9" />
                </div>
              </CardContent>
            </Card>
            <DataTable
              columns={columns}
              data={data.items}
              rowKey={(r) => r.id}
              loading={loading}
              empty={<EmptyState icon={PackageX} title="No failed deliveries 🎉" description="Shipments the courier couldn't deliver show up here for you to reattempt, return, or dispute." />}
              pagination={totalPages > 1 ? { page, pages: totalPages, total: data.total, pageSize: PAGE_SIZE, onPageChange: setPage } : undefined}
            />
          </>
        )}
      </div>

      {/* Action modal */}
      <Dialog open={!!target} onOpenChange={(v) => !v && setTarget(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>NDR action — {target?.order_number}</DialogTitle>
          </DialogHeader>
          {target && (
            <div className="space-y-4">
              {target.ndr_reason && (
                <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> <span>Courier reason: {target.ndr_reason}</span>
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Action</Label>
                <Select value={action} onValueChange={setAction}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ACTIONS.map((a) => <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">{ACTIONS.find((a) => a.id === action)?.desc}</p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="ndr-comments" className="text-xs text-slate-600">Comments <span className="text-red-500">*</span></Label>
                <Textarea id="ndr-comments" rows={2} value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} placeholder="e.g. Buyer confirmed availability tomorrow" />
              </div>

              {action === "reattempt" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600">Preferred date</Label>
                    <Input type="date" value={form.preferredDate} onChange={(e) => setForm({ ...form, preferredDate: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600">Updated phone</Label>
                    <Input inputMode="numeric" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} />
                  </div>
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label className="text-xs text-slate-600">Updated address</Label>
                    <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600">Address type</Label>
                    <Select value={form.addressType} onValueChange={(v) => setForm({ ...form, addressType: v })}>
                      <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="home">Home</SelectItem>
                        <SelectItem value="office">Office</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {action === "fake_attempt" && (
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600">Proof image URL <span className="text-red-500">*</span></Label>
                    <Input value={form.proofImageUrl} onChange={(e) => setForm({ ...form, proofImageUrl: e.target.value })} placeholder="https://…/proof.jpg" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600">Remarks <span className="text-red-500">*</span></Label>
                    <Input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} placeholder="Why this attempt was false" />
                  </div>
                  <p className="text-xs text-slate-500">Dispute isn't available for every courier — if it isn't, choose reattempt or return.</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)} disabled={saving}>Cancel</Button>
            <Button onClick={submit} disabled={saving}>{saving ? "Submitting…" : "Submit action"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SellerLayout>
  );
};

export default B2CNdr;
